// v5.77/79 — in-place Creative Edit surface, rendered INSIDE the ImageNode
// tile. Prepares are concurrent: any tile with an in-flight prepare shows its
// spinner here; only the ACTIVE tile renders the full Konva editor (controls
// in the right SidebarPanel). Shared state in creative-edit-store.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Group, Rect, Image as KImage, Text as KText, Transformer } from 'react-konva';
import type Konva from 'konva';
import { useCreativeEditStore } from './creative-edit-store';
import { useToast } from '@/hooks/use-toast';
import { creativeFontFamily, creativeFontWeight } from './creative-edit-fonts';

/** Plate loader WITH crossOrigin — the plate is always on Bunny (CORS *) and
 *  toDataURL needs an untainted stage. use-konva-image must NOT set
 *  crossOrigin (WaveSpeed CloudFront tiles); this one must. */
function useCorsImage(src: string | null): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!src) { setImg(null); return; }
    let alive = true;
    const el = new window.Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => { if (alive) setImg(el); };
    el.src = src;
    return () => { alive = false; };
  }, [src]);
  return img;
}

interface Props {
  imageId: string;
  width: number;   // tile displayWidth
  height: number;  // tile displayHeight
}

export function CreativeEditNodeOverlay({ imageId, width, height }: Props) {
  const { toast } = useToast();
  const prepare = useCreativeEditStore((s) => s.prepares[imageId]);
  const isActive = useCreativeEditStore((s) => s.activeImageId === imageId);
  const selectedTextId = useCreativeEditStore((s) => s.selectedTextId);
  const editingTextId = useCreativeEditStore((s) => s.editingTextId);
  const fontsVersion = useCreativeEditStore((s) => s.fontsVersion);
  const convertingIds = useCreativeEditStore((s) => s.convertingIds);
  const { setStage, setSelected, setEditingText, updateText, convertBlock, close, dismiss } = useCreativeEditStore.getState();

  const makeEditable = async (textId: string) => {
    setSelected(textId);
    const error = await convertBlock(textId);
    if (error) toast({ title: 'Could not make editable', description: error, variant: 'destructive' });
  };

  const stageRef = useRef<Konva.Stage>(null);
  const trRef = useRef<Konva.Transformer>(null);
  const nodeRefs = useRef<Map<string, Konva.Group>>(new Map());
  // Line-width measurement for highlight bands (fonts already loaded by the
  // store before phase flips to ready).
  const measureCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const measureLine = (ln: string, family: string, weight: string, italic: string, fontSize: number): number => {
    if (!measureCtxRef.current) measureCtxRef.current = document.createElement('canvas').getContext('2d');
    const mctx = measureCtxRef.current;
    if (!mctx) return 0;
    mctx.font = `${italic}${weight} ${fontSize}px "${creativeFontFamily(family, doc ?? {})}", sans-serif`;
    return mctx.measureText(ln).width;
  };
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  const doc = prepare?.doc ?? null;
  const scale = doc ? width / doc.width : 1;
  // v5.83 — the stage renders at the DOC's aspect (anchored to tile width),
  // not the tile box: tile display dims can drift a few px (or be user-
  // resized) off the image ratio, and a tile-shaped stage exported a
  // tile-shaped PNG (768×1379 from a 768×1376 source — Dan: "ratio or size
  // is different than the original").
  const stageH = doc ? Math.round(width * (doc.height / doc.width)) : height;
  const plate = useCorsImage(isActive ? doc?.plateUrl ?? null : null);

  // Register this stage for export while this tile is the active editor.
  useEffect(() => {
    if (!isActive) return;
    setStage(stageRef.current);
    return () => setStage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, doc]);

  // Transformer follows selection
  useEffect(() => {
    if (!isActive) return;
    const tr = trRef.current;
    if (!tr) return;
    const selDoc = doc?.texts.find((x) => x.id === selectedTextId);
    const node = selectedTextId && !editingTextId && selDoc?.struck ? nodeRefs.current.get(selectedTextId) : null;
    tr.nodes(node ? [node] : []);
    tr.getLayer()?.batchDraw();
  }, [isActive, selectedTextId, editingTextId, doc, fontsVersion]);

  const editing = isActive ? doc?.texts.find((t) => t.id === editingTextId) ?? null : null;

  const commitInlineEdit = () => {
    if (editingTextId && editTextareaRef.current) {
      updateText(editingTextId, { text: editTextareaRef.current.value });
    }
    setEditingText(null);
  };

  // Escape (active tile only): close inline edit first, then exit the editor.
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const s = useCreativeEditStore.getState();
      if (s.activeImageId !== imageId) return;
      e.stopPropagation();
      if (s.editingTextId) commitInlineEdit();
      else {
        close();
        void import('./canvas-store').then((m) => m.useCanvasStore.getState().closeSidebarPanel());
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, imageId, editingTextId]);

  const fontStyleHas = (style: string, flag: 'bold' | 'italic') => (flag === 'bold' ? /bold|800|900/.test(style) : style.includes(flag));
  const inlineEditorStyle = useMemo(() => {
    if (!editing) return null;
    return {
      left: editing.x * scale,
      top: editing.y * scale,
      width: Math.max(40, editing.w * scale + 6),
      fontFamily: `"${creativeFontFamily(editing.fontFamily, doc ?? {})}", sans-serif`,
      fontSize: editing.fontSize * scale,
      letterSpacing: `${(editing.letterSpacing ?? 0) * scale}px`,
      lineHeight: editing.lineHeight,
      color: editing.fill,
      textAlign: editing.align,
      fontWeight: creativeFontWeight(editing.fontStyle),
      fontStyle: fontStyleHas(editing.fontStyle, 'italic') ? 'italic' : 'normal',
    } as React.CSSProperties;
  }, [editing, scale, doc]);

  if (!prepare) return null;

  if (prepare.phase === 'preparing') {
    return (
      <div className="nodrag" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#F5F3EE' }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', border: '2.5px solid #D3CDC2', borderTopColor: '#8A8377', animation: 'imgnode-spin 0.8s linear infinite' }} />
        <div style={{ fontSize: 11, color: '#8A8377', fontWeight: 500, textAlign: 'center', padding: '0 12px' }}>{prepare.phaseNote}</div>
        <style>{`@keyframes imgnode-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (prepare.phase === 'failed') {
    return (
      <div className="nodrag" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#F5F3EE', padding: 12 }}>
        <div style={{ fontSize: 11, color: '#EF4444', textAlign: 'center' }}>{prepare.error}</div>
        <button
          className="nodrag"
          onClick={() => dismiss(imageId)}
          style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E2DC', background: '#fff', cursor: 'pointer', color: '#1A1A1A' }}
        >
          Close
        </button>
      </div>
    );
  }

  // ready + not active: the tile shows its normal image (overlay hidden by
  // ImageNode); nothing to render here.
  if (!isActive || !doc) return null;

  return (
    <div
      className="nodrag"
      data-testid={`creative-edit-overlay-${imageId}`}
      style={{ position: 'relative', width: '100%', height: '100%', cursor: 'default' }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <Stage
        ref={stageRef}
        width={width}
        height={stageH}
        scaleX={scale}
        scaleY={scale}
        onMouseDown={(e) => {
          if (e.target === e.target.getStage() || e.target.className === 'Image') {
            setSelected(null);
            if (editingTextId) commitInlineEdit();
          }
        }}
      >
        <Layer>
          {plate && <KImage image={plate} width={doc.width} height={doc.height} />}
          {doc.texts.map((t) => {
            if (!t.struck) {
              // v5.90 — original pixels still show this text (100% identical).
              // A hit area selects it; double-click makes it editable.
              const isConverting = convertingIds.includes(t.id);
              const isSel = selectedTextId === t.id;
              return (
                <Rect
                  key={`hit-${t.id}`}
                  x={t.x}
                  y={t.y}
                  width={t.w}
                  height={t.h}
                  fill={isConverting ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.001)'}
                  stroke={isSel || isConverting ? '#6366F1' : undefined}
                  strokeWidth={2 / scale}
                  dash={[8 / scale, 5 / scale]}
                  onClick={() => setSelected(t.id)}
                  onTap={() => setSelected(t.id)}
                  onDblClick={() => { void makeEditable(t.id); }}
                  onDblTap={() => { void makeEditable(t.id); }}
                />
              );
            }
            const linesArr = t.text.split('\n');
            const weight = String(creativeFontWeight(t.fontStyle));
            const italic = t.fontStyle.includes('italic') ? 'italic ' : '';
            return (
              <Group
                key={`${t.id}-f${fontsVersion}`}
                ref={(node) => {
                  if (node) nodeRefs.current.set(t.id, node);
                  else nodeRefs.current.delete(t.id);
                }}
                visible={editingTextId !== t.id}
                x={t.x}
                y={t.y}
                rotation={t.rotation ?? 0}
                draggable
                onDragEnd={(e) => updateText(t.id, { x: Math.round(e.target.x()), y: Math.round(e.target.y()) })}
                onClick={() => setSelected(t.id)}
                onTap={() => setSelected(t.id)}
                onDblClick={() => { setSelected(t.id); setEditingText(t.id); }}
                onDblTap={() => { setSelected(t.id); setEditingText(t.id); }}
                onTransformEnd={(e) => {
                  const node = e.target as Konva.Group;
                  updateText(t.id, {
                    x: Math.round(node.x()),
                    y: Math.round(node.y()),
                    w: Math.max(20, Math.round(t.w * node.scaleX())),
                    rotation: Math.round(node.rotation()),
                  });
                  node.scaleX(1);
                  node.scaleY(1);
                }}
              >
                {t.bgFill && linesArr.map((ln, i) => {
                  if (!ln.trim()) return null;
                  const lw = measureLine(ln, t.fontFamily, weight, italic, t.fontSize);
                  const pad = t.fontSize * 0.32;
                  const bandH = t.fontSize * 1.16;
                  const bx = t.align === 'center' ? (t.w - lw) / 2 - pad : t.align === 'right' ? t.w - lw - pad : -pad;
                  const by = i * t.fontSize * t.lineHeight + ((t.lineHeight - 1.16) * t.fontSize) / 2;
                  return <Rect key={i} x={bx} y={by} width={lw + pad * 2} height={bandH} fill={t.bgFill} cornerRadius={t.fontSize * 0.14} listening={false} />;
                })}
                <KText
                  text={t.text}
                  x={0}
                  y={0}
                  width={t.w}
                  fontFamily={`"${creativeFontFamily(t.fontFamily, doc)}", sans-serif`}
                  fontSize={t.fontSize}
                  fontStyle={t.fontStyle}
                  fill={t.fill}
                  stroke={t.stroke}
                  strokeWidth={t.strokeWidth ?? 0}
                  fillAfterStrokeEnabled
                  align={t.align}
                  lineHeight={t.lineHeight}
                  letterSpacing={t.letterSpacing ?? 0}
                  wrap="none"
                />
              </Group>
            );
          })}
          <Transformer
            ref={trRef}
            enabledAnchors={['middle-left', 'middle-right']}
            rotateEnabled
            borderStroke="#6366F1"
            anchorStroke="#6366F1"
            anchorFill="#ffffff"
            anchorSize={7}
            boundBoxFunc={(oldBox, newBox) => (newBox.width < 20 ? oldBox : newBox)}
          />
        </Layer>
      </Stage>
      {editing && inlineEditorStyle && (
        <textarea
          ref={editTextareaRef}
          autoFocus
          defaultValue={editing.text}
          wrap="off"
          onBlur={commitInlineEdit}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') commitInlineEdit();
            e.stopPropagation();
          }}
          className="nodrag"
          style={{
            ...inlineEditorStyle,
            position: 'absolute',
            zIndex: 10,
            background: editing.bgFill ?? 'rgba(0,0,0,0.55)',
            border: '1px dashed #6366F1',
            borderRadius: 2,
            padding: '1px 3px',
            outline: 'none',
            resize: 'none',
            overflow: 'hidden',
            whiteSpace: 'pre',
            minHeight: (editing.fontSize * editing.lineHeight * scale) + 6,
          }}
          rows={Math.max(1, editing.text.split('\n').length)}
        />
      )}
    </div>
  );
}
